import React from "react";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import { Typography } from "@mui/material";
import { AxiosError, AxiosResponse } from "axios";
import { NavigateFunction } from "react-router-dom";
import { OrangeTextButton } from "../../../common/OrangeTextButton";
import {
  EMPTY_SITE_WEBHOOK_FORM_VALUES,
  SiteWebhookFormFields,
  SiteWebhookFormValues,
} from "../../../common/SiteWebhookFormFields";
import { createApi } from "../../../utils/createApi";
import { universalAPIErrorHandler } from "../../../utils/universalAPIErrorHandler";
import { getSupportUrl } from "../../../utils/supportUrl";
import {
  WebhookFieldErrors,
  authBodyJsonError,
  buildWebhookAuthCreatePayload,
  extractWebhookFieldErrors,
} from "../../../utils/webhookAuth";
import { useSiteAboutStore } from "./siteAboutStore/siteAboutStore";
import { SiteWebhook, jsonTextError } from "./SiteAboutPageUtils/siteWebhooks";

export const buildSiteWebhookCreatePayload = (
  siteId: number,
  values: SiteWebhookFormValues
) => {
  const { auth, ...rest } = values;
  return {
    customer: siteId,
    ...rest,
    ...buildWebhookAuthCreatePayload(auth),
  };
};

/**
 * Site About → Webhooks → "+ Add Webhook" (ENG-2233 / SUP-757).
 *
 * Posts a new WebhookCustomer for the site at any time after onboarding —
 * the same shape the onboarding Webhooks step sends. The server sets the
 * webhook type to Custom. Open state lives in the site-about store so the
 * tab can open it without more prop plumbing.
 */
export const AddSiteWebhookDialog = ({
  siteId,
  onCreated,
  navigate,
}: {
  siteId: number;
  onCreated: (webhook: SiteWebhook) => void;
  navigate: NavigateFunction;
}) => {
  const open = useSiteAboutStore((state) => state.addSiteWebhookDialogOpen);
  const setOpen = useSiteAboutStore(
    (state) => state.setAddSiteWebhookDialogOpen
  );

  const [values, setValues] = React.useState<SiteWebhookFormValues>(
    EMPTY_SITE_WEBHOOK_FORM_VALUES
  );
  const [fieldErrors, setFieldErrors] = React.useState<WebhookFieldErrors>({});
  const [submitting, setSubmitting] = React.useState(false);

  const reset = () => {
    setValues(EMPTY_SITE_WEBHOOK_FORM_VALUES);
    setFieldErrors({});
    setSubmitting(false);
  };

  const close = () => {
    setOpen(false);
    reset();
  };

  const submit = () => {
    const AxiosUI = createApi(``);
    const payload = buildSiteWebhookCreatePayload(siteId, values);
    setSubmitting(true);
    setFieldErrors({});
    AxiosUI.post("webhook_customer/", payload).then(
      (response: AxiosResponse) => {
        onCreated(response.data);
        close();
      },
      (reason: AxiosError) => {
        setSubmitting(false);
        // A 400 names the rejected fields (an auth_endpoint refused by the
        // SSRF guard, an auth_body that can't authenticate, ...): keep the
        // input and show the messages inline on those fields.
        const nextFieldErrors = extractWebhookFieldErrors(reason);
        if (Object.keys(nextFieldErrors).length > 0) {
          setFieldErrors(nextFieldErrors);
          return;
        }
        universalAPIErrorHandler(reason, navigate, submit, () => {
          alert("Error adding the webhook. Please try again.");
        });
      }
    );
  };

  const submitDisabled =
    submitting ||
    !values.display_name.trim() ||
    !values.target_url.trim() ||
    Boolean(authBodyJsonError(values.auth.auth_body)) ||
    Boolean(jsonTextError(values.message_body, "Message Body"));

  return (
    <Dialog
      open={open}
      onClose={close}
      aria-labelledby="add-site-webhook-dialog-title"
    >
      <DialogTitle
        id="add-site-webhook-dialog-title"
        sx={{ textAlign: "center" }}
      >
        Add Site Webhook
      </DialogTitle>
      <DialogContent>
        <div className="pt-[6px]">
          <SiteWebhookFormFields
            values={values}
            fieldErrors={fieldErrors}
            width={500}
            messageBodyHelperText="JSON sent to the webhook. Use the placeholders from the support page."
            onFieldChange={(field, value) => {
              setValues((previousState) => ({
                ...previousState,
                [field]: value,
              }));
            }}
            onAuthFieldChange={(field, value) => {
              setValues((previousState) => ({
                ...previousState,
                auth: { ...previousState.auth, [field]: value },
              }));
            }}
          />
        </div>
        <Typography variant="caption" sx={{ mt: 2, display: "block" }}>
          Display Name and Target URL are required. The webhook type is set to
          Custom automatically. Formatting examples are on our support page:{" "}
          <a
            href={getSupportUrl("/admin_ui/webhooks/")}
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: "#1976d2", textDecoration: "none" }}
          >
            Webhooks
          </a>
          . Cameras pick which site webhook they send to under Edit Camera →
          Alert Settings → Add Stream Webhook.
        </Typography>
      </DialogContent>
      <DialogActions>
        <OrangeTextButton onClick={close} text="Cancel" disabled={false} />
        <OrangeTextButton
          onClick={submit}
          text="Submit"
          disabled={submitDisabled}
        />
      </DialogActions>
    </Dialog>
  );
};
